import { IsoTimestampSchema } from "./string-validation"
import { z } from "zod"

import { createRequiredPersonNameSchema } from "./person-name"

export const ROLE_KEYS = [
	"super_admin",
	"support_manager",
	"support_staff",
	"system_ecrypt_webhook",
] as const

export const PERMISSION_KEYS = [
	"access.roles.read",
	"access.roles.manage",
	"access.assignments.read",
	"access.assignments.manage",
	"orders.read",
	"orders.command.support",
	"orders.command.manager",
	"orders.command.ops",
	"orders.command.fulfillment",
	"memberships.manage",
	"payments.read",
	"payments.command.approve",
	"payments.command.integrations",
	"payments.refunds.manage", // Retained for historical grant/audit records; not an available staff permission.
	"patients.read.basic",
	"patients.read.phi",
	"patients.manage",
	"patients.deactivate",
	"audit.read",
	"lab.ai.settings.read",
	"products.read",
	"products.manage",
] as const

export const PORTAL_ADMIN_USER_MFA_STATES = [
	"not_configured",
	"enabled",
	"preferred",
	"unknown",
] as const

export const WORKFLOW_ACTOR_ROLE_KEYS = ["admin", "ops", "support", "clinician", "fulfillment"] as const

export const RoleKeySchema = z.enum(ROLE_KEYS).meta({ id: "RoleKey" })
export type RoleKey = z.infer<typeof RoleKeySchema>
export const PermissionKeySchema = z.enum(PERMISSION_KEYS).meta({ id: "PermissionKey" })
export type PermissionKey = z.infer<typeof PermissionKeySchema>
export const PortalAdminUserMfaStateSchema = z.enum(PORTAL_ADMIN_USER_MFA_STATES).meta({
	id: "PortalAdminUserMfaState",
})
export type PortalAdminUserMfaState = z.infer<typeof PortalAdminUserMfaStateSchema>
export const WorkflowActorRoleKeySchema = z.enum(WORKFLOW_ACTOR_ROLE_KEYS).meta({
	id: "WorkflowActorRoleKey",
})
export type WorkflowActorRoleKey = z.infer<typeof WorkflowActorRoleKeySchema>

export const RoleAssignmentSchema = z.object({
	id: z.uuid(),
	userId: z.uuid(),
	roleKey: RoleKeySchema,
	grantedByUserId: z.uuid().optional(),
	revokedAt: IsoTimestampSchema.nullable().optional(),
	createdAt: IsoTimestampSchema,
	updatedAt: IsoTimestampSchema,
}).meta({ id: "RoleAssignment" })
export type RoleAssignment = z.infer<typeof RoleAssignmentSchema>

export const RoleCatalogFamilySchema = z.enum(["admin", "support", "system"]).meta({
	id: "RoleCatalogFamily",
})
export type RoleCatalogFamily = z.infer<typeof RoleCatalogFamilySchema>

export const AccessRoleDefinitionSchema = z.object({
	roleKey: RoleKeySchema,
	label: z.string(),
	family: RoleCatalogFamilySchema,
	summary: z.string(),
	highlights: z.array(z.string()),
	technicalNotes: z.array(z.string()),
	riskNotes: z.array(z.string()).default([]),
	workflowActorRole: WorkflowActorRoleKeySchema.nullable().default(null),
	isEditable: z.boolean(),
	permissionGrantsEditable: z.boolean(),
	isSystemRole: z.boolean().default(false),
}).meta({ id: "AccessRoleDefinition" })
export type AccessRoleDefinition = z.infer<typeof AccessRoleDefinitionSchema>

export const AccessPermissionDefinitionSchema = z.object({
	permissionKey: PermissionKeySchema,
	label: z.string(),
	description: z.string(),
	domainKey: z.string(),
	riskLevel: z.enum(["low", "medium", "high", "critical"]),
}).meta({ id: "AccessPermissionDefinition" })
export type AccessPermissionDefinition = z.infer<typeof AccessPermissionDefinitionSchema>

export const AccessRolePermissionGrantSchema = z.object({
	roleKey: RoleKeySchema,
	permissionKey: PermissionKeySchema,
}).meta({ id: "AccessRolePermissionGrant" })
export type AccessRolePermissionGrant = z.infer<typeof AccessRolePermissionGrantSchema>

export const AccessRolePermissionHistoryEntrySchema = z.object({
	id: z.uuid(),
	roleKey: RoleKeySchema,
	changedByUserId: z.uuid(),
	changeSummary: z.string(),
	addedPermissionKeys: z.array(PermissionKeySchema),
	removedPermissionKeys: z.array(PermissionKeySchema),
	createdAt: IsoTimestampSchema,
}).meta({ id: "AccessRolePermissionHistoryEntry" })
export type AccessRolePermissionHistoryEntry = z.infer<typeof AccessRolePermissionHistoryEntrySchema>

export const PortalAuthMeSchema = z.object({
	userId: z.uuid(),
	cognitoSub: z.string(),
	roles: z.array(RoleKeySchema),
	assignments: z.array(RoleAssignmentSchema),
	permissions: z.array(PermissionKeySchema),
	roleDefinitions: z.array(AccessRoleDefinitionSchema),
}).meta({ id: "PortalAuthMe" })
export type PortalAuthMe = z.infer<typeof PortalAuthMeSchema>

export const PortalAdminUserSummarySchema = z.object({
	id: z.uuid(),
	cognitoSub: z.string(),
	cognitoPool: z.literal("internal"),
	disabled: z.boolean(),
	createdAt: IsoTimestampSchema,
	updatedAt: IsoTimestampSchema,
	email: z.email().nullable(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	phone: z.string().nullable(),
	mfaState: PortalAdminUserMfaStateSchema,
	roles: z.array(RoleKeySchema),
	assignments: z.array(RoleAssignmentSchema),
}).meta({ id: "PortalAdminUserSummary" })
export type PortalAdminUserSummary = z.infer<typeof PortalAdminUserSummarySchema>
export const PortalAdminUserDetailSchema = PortalAdminUserSummarySchema.meta({ id: "PortalAdminUserDetail" })
export type PortalAdminUserDetail = z.infer<typeof PortalAdminUserDetailSchema>
export const PortalAdminUserListResponseSchema = z.object({
	data: z.array(PortalAdminUserSummarySchema),
}).meta({ id: "PortalAdminUserListResponse" })
export type PortalAdminUserListResponse = z.infer<typeof PortalAdminUserListResponseSchema>
export const PortalAdminUserDetailResponseSchema = z.object({
	user: PortalAdminUserDetailSchema,
}).meta({ id: "PortalAdminUserDetailResponse" })
export type PortalAdminUserDetailResponse = z.infer<typeof PortalAdminUserDetailResponseSchema>

export const InvitePortalUserSchema = z.object({
	firstName: createRequiredPersonNameSchema("First name"),
	lastName: createRequiredPersonNameSchema("Last name"),
	email: z.string().trim().pipe(z.email()),
	roleKey: RoleKeySchema,
}).meta({ id: "InvitePortalUser" })
export type InvitePortalUser = z.infer<typeof InvitePortalUserSchema>
export const PortalAdminUserInviteResponseSchema = z.object({
	user: PortalAdminUserDetailSchema,
}).meta({ id: "PortalAdminUserInviteResponse" })
export type PortalAdminUserInviteResponse = z.infer<typeof PortalAdminUserInviteResponseSchema>
export const PortalAdminIdentityProviderSyncStatusSchema = z
	.enum(["synced", "partial", "not_found", "failed"])
	.meta({ id: "PortalAdminIdentityProviderSyncStatus" })
export type PortalAdminIdentityProviderSyncStatus = z.infer<
	typeof PortalAdminIdentityProviderSyncStatusSchema
>
export const PortalAdminUserDisableResponseSchema = z.object({
	ok: z.literal(true),
	accessRevoked: z.literal(true),
	identityProviderSync: PortalAdminIdentityProviderSyncStatusSchema,
}).meta({
	id: "PortalAdminUserDisableResponse",
})
export type PortalAdminUserDisableResponse = z.infer<typeof PortalAdminUserDisableResponseSchema>

export const RoleCapabilityStatusSchema = z.enum(["allowed", "denied"]).meta({ id: "RoleCapabilityStatus" })
export type RoleCapabilityStatus = z.infer<typeof RoleCapabilityStatusSchema>
export const RoleCatalogRoleSchema = AccessRoleDefinitionSchema.extend({
	permissions: z.array(PermissionKeySchema),
}).meta({ id: "RoleCatalogRole" })
export type RoleCatalogRole = z.infer<typeof RoleCatalogRoleSchema>
export const RoleCatalogCapabilitySchema = z.object({
	capabilityKey: z.string(),
	label: z.string(),
	description: z.string(),
	statusesByRole: z.record(RoleKeySchema, RoleCapabilityStatusSchema),
	notesByRole: z.partialRecord(RoleKeySchema, z.string()).default({}),
	permissionSources: z.array(PermissionKeySchema),
	technicalNotes: z.array(z.string()).default([]),
}).meta({ id: "RoleCatalogCapability" })
export type RoleCatalogCapability = z.infer<typeof RoleCatalogCapabilitySchema>
export const RoleCatalogCapabilityGroupSchema = z.object({
	groupKey: z.string(),
	label: z.string(),
	capabilities: z.array(RoleCatalogCapabilitySchema),
}).meta({ id: "RoleCatalogCapabilityGroup" })
export type RoleCatalogCapabilityGroup = z.infer<typeof RoleCatalogCapabilityGroupSchema>
export const PortalRoleCatalogSchema = z.object({
	roles: z.array(RoleCatalogRoleSchema),
	capabilityGroups: z.array(RoleCatalogCapabilityGroupSchema),
	permissionDefinitions: z.array(AccessPermissionDefinitionSchema),
}).meta({ id: "PortalRoleCatalog" })
export type PortalRoleCatalog = z.infer<typeof PortalRoleCatalogSchema>

export const AccessRoleDetailResponseSchema = z.object({
	role: RoleCatalogRoleSchema,
	permissionDefinitions: z.array(AccessPermissionDefinitionSchema),
}).meta({ id: "AccessRoleDetailResponse" })
export type AccessRoleDetailResponse = z.infer<typeof AccessRoleDetailResponseSchema>
export const AccessRolePermissionHistoryResponseSchema = z.object({
	data: z.array(AccessRolePermissionHistoryEntrySchema),
}).meta({ id: "AccessRolePermissionHistoryResponse" })
export type AccessRolePermissionHistoryResponse = z.infer<typeof AccessRolePermissionHistoryResponseSchema>
export const UpdateAccessRolePermissionsSchema = z.object({
	permissionKeys: z.array(PermissionKeySchema),
	changeSummary: z.string().min(1),
}).meta({ id: "UpdateAccessRolePermissions" })
export type UpdateAccessRolePermissions = z.infer<typeof UpdateAccessRolePermissionsSchema>

export const CreateRoleAssignmentSchema = z.object({
	userId: z.uuid(),
	roleKey: RoleKeySchema,
}).meta({ id: "CreateRoleAssignment" })
export type CreateRoleAssignment = z.infer<typeof CreateRoleAssignmentSchema>
